/**
 * Map Plugin for Search Results Map Variant
 *
 * Owns the Google Maps instance lifecycle: creation, camera-to-store coordinate
 * dispatch, and teardown.
 *
 * Single responsibility: create and manage the map canvas.
 *
 * This plugin does NOT:
 * - Re-center the map when the search origin changes (see mapViewport.plugin)
 * - Handle search-within-map flows (see searchWithinMap.plugin)
 * - Sync markers or boundaries (see markers.plugin, mapBoundaries.plugin)
 *
 * Reason to change: how the map is initialised, configured, or how the idle
 * event translates into a Redux coordinate update.
 *
 * @module searchResultsMap/plugins/map.plugin
 */

import { selectLocation } from 'features/searchResultsFacets';
import { map, take } from 'rxjs';
import mapDataSlice from '../redux/dataSlice';
import { DEFAULT_MAX_ZOOM, DEFAULT_MIN_ZOOM, DEFAULT_ZOOM_LEVELS } from '../constants';

const { actions: dataActions } = mapDataSlice;

const defaultMapSettings = {
  clickableIcons: false,
  disableDefaultUI: true,
  disableDoubleClickZoom: false,
  gestureHandling: 'greedy',
  minZoom: DEFAULT_MIN_ZOOM,
  maxZoom: DEFAULT_MAX_ZOOM,
  panControl: false,
  scrollwheel: true,
  zoomControl: true,
  zoomControlStyle: 'SMALL',
  mapId: "65cf3dc0be4e0b76",
};

export default function createSearchResultsMapPlugin({ onMapLoaded } = {}) {
  const state = {
    started: false,
    mapCreated: false,
    mapFacade: null,
    mapContainer: null,
    store$: null,
    dispatch: null,
    lastBounds: null,
    subscriptions: [],
    mapEventUnsubscribers: []
  };

  function onCoordinatesChanged({ bounds }) {
    const { east, north, south, west } = bounds;
    const last = state.lastBounds;

    if (
      last &&
      last.east === east &&
      last.north === north &&
      last.south === south &&
      last.west === west
    ) {
      return;
    }

    state.lastBounds = { east, north, south, west };
    state.dispatch(
      dataActions.updateCoordinates({
        east,
        north,
        south,
        west
      })
    );
  }

  function setupMap({ mapFacade }, mapSettings, eventUnsubscribers) {
    mapFacade.createMap({
      container: state.mapContainer,
      options: mapSettings,
    });

    const onMapLoadedUnsubscribe = mapFacade.onceMapEvent('idle', () => onMapLoaded?.());

    const idleUnsubscribe =
      mapFacade.onMapEvent('idle', () => {
        // Snapshot is read synchronously while Maps state is fresh, but the Redux
        // dispatch is deferred to a new task so the browser can handle any queued
        // user input (e.g. marker clicks) before the store emission cascade runs.
        const snapshot = mapFacade.getCameraSnapshot();
        setTimeout(() => onCoordinatesChanged({ bounds: snapshot.bounds }), 0);
      });

    eventUnsubscribers.push(
      onMapLoadedUnsubscribe,
      idleUnsubscribe
    );
  }

  function getMapSettings(locationParameters) {
      const zoomLevel = DEFAULT_ZOOM_LEVELS[locationParameters.locationType] ?? DEFAULT_ZOOM_LEVELS.default;

      return {
          ...defaultMapSettings,
          zoom: zoomLevel,
          center: {
              lat: +locationParameters.originLatitude,
              lng: +locationParameters.originLongitude
          }
      };
  }

  function handleMapInitialization(mapSettings) {
    setupMap(
      {
        mapFacade: state.mapFacade,
        mapContainer: state.mapContainer,
      },
      mapSettings,
      state.mapEventUnsubscribers
    );

    state.mapCreated = true;
  }

  function subscribeToStoreStreams() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocation),
        take(1),
        map(getMapSettings))
        .subscribe(handleMapInitialization)
    );
  }

  function clearSubscriptions() {
    state.subscriptions.forEach(subscription => {
      subscription?.unsubscribe?.();
    });
    state.subscriptions = [];
  }

  function clearMapEventSubscriptions() {
    state.mapEventUnsubscribers.forEach(unsubscribe => {
      unsubscribe?.();
    });
    state.mapEventUnsubscribers = [];
  }

  return {
    name: 'search-results-map',

    async init(context) {
      context.state.searchResultsMap = {
        started: false,
        ready: false
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      if (!state.mapFacade) {
        return;
      }

      state.mapContainer = context.root;
      state.store$ = context.services.store$;
      state.dispatch = context.services.dispatch;

      // Map creation is a one-time operation: the map persists across
      // start/stop cycles once created.
      if (!state.mapCreated) {
        subscribeToStoreStreams();
      }

      state.started = true;
      context.state.searchResultsMap = {
        started: true,
        ready: true
      };
    },

    async stop(context) {
      clearSubscriptions();
      clearMapEventSubscriptions();

      state.mapFacade?.destroyMap?.();

      state.mapContainer = null;
      state.mapFacade = null;
      state.store$ = null;
      state.dispatch = null;
      state.lastBounds = null;
      state.mapCreated = false;
      state.started = false;

      delete context.state.searchResultsMap;
    }
  };
}
